import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { assetApi } from "../api";
import { useAsyncData, useMutation } from "../hooks";
import { AssetError, AssetLoading, BackBtn, Btn, Card, CardHeader, PageHeader } from "../components/AssetUI";
import type { AssetUpdateRequest } from "../types";

const inp = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold text-slate-500">{label}</span>
      {children}
    </label>
  );
}

export default function AssetEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAsyncData(() => assetApi.getAsset(Number(id)), [id]);
  const cats = useAsyncData(() => assetApi.listCategories(), []);
  const locs = useAsyncData(() => assetApi.listLocations(), []);
  const [form, setForm] = useState<AssetUpdateRequest>({});
  const save = useMutation(() => assetApi.updateAsset(Number(id), form));

  useEffect(() => {
    if (!data) return;
    setForm({
      name: data.name, assetNumber: data.assetNumber, categoryId: data.categoryId,
      locationId: data.locationId ?? undefined, serialNumber: data.serialNumber ?? undefined, brand: data.brand ?? undefined,
      model: data.model ?? undefined, description: data.description ?? undefined, purchaseDate: data.purchaseDate ?? undefined,
      purchaseCost: data.purchaseCost ?? undefined, currentValue: data.currentValue ?? undefined,
      warrantyExpiry: data.warrantyExpiry ?? undefined, condition: data.condition,
      depreciationMethod: data.depreciationMethod ?? undefined,
    });
  }, [data]);

  function update<K extends keyof AssetUpdateRequest>(k: K, v: AssetUpdateRequest[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    await save.mutate(undefined as never);
    navigate(`/assets/${id}`);
  }

  if (loading) return <AssetLoading label="Loading asset…" />;
  if (error)   return <AssetError message={error} onRetry={reload} />;

  return (
    <div className="space-y-6">
      <PageHeader breadcrumb={`Asset Management · ${data?.assetNumber ?? ""}`} title={`Edit ${data?.name ?? "Asset"}`}
        actions={<BackBtn to={`/assets/${id}`} label="Back to Asset" />}
      />
      <form onSubmit={submit} className="space-y-5">
        <Card noPad>
          <CardHeader title="Basic Information" />
          <div className="grid gap-4 p-5 md:grid-cols-2">
            <Field label="Asset Name *"><input required className={inp} value={form.name ?? ""} onChange={(e) => update("name", e.target.value)} /></Field>
            <Field label="Asset Code *"><input required className={inp} value={form.assetNumber ?? ""} onChange={(e) => update("assetNumber", e.target.value)} /></Field>
            <Field label="Category">
              <select className={inp} value={form.categoryId ?? ""} onChange={(e) => update("categoryId", Number(e.target.value))}>
                {cats.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Location">
              <select className={inp} value={form.locationId ?? ""} onChange={(e) => update("locationId", Number(e.target.value))}>
                {locs.data?.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </Field>
            <Field label="Serial Number"><input className={inp} value={form.serialNumber ?? ""} onChange={(e) => update("serialNumber", e.target.value)} /></Field>
            <Field label="Condition">
              <select className={inp} value={form.condition ?? "GOOD"} onChange={(e) => update("condition", e.target.value as any)}>
                {["EXCELLENT","GOOD","FAIR","POOR","DAMAGED"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Brand"><input className={inp} value={form.brand ?? ""} onChange={(e) => update("brand", e.target.value)} /></Field>
            <Field label="Model"><input className={inp} value={form.model ?? ""} onChange={(e) => update("model", e.target.value)} /></Field>
          </div>
        </Card>

        <Card noPad>
          <CardHeader title="Financial Details" />
          <div className="grid gap-4 p-5 md:grid-cols-2">
            <Field label="Purchase Date"><input type="date" className={inp} value={form.purchaseDate ?? ""} onChange={(e) => update("purchaseDate", e.target.value)} /></Field>
            <Field label="Warranty Expiry"><input type="date" className={inp} value={form.warrantyExpiry ?? ""} onChange={(e) => update("warrantyExpiry", e.target.value)} /></Field>
            <Field label="Purchase Cost (KES)"><input type="number" min={0} className={inp} value={form.purchaseCost ?? ""} onChange={(e) => update("purchaseCost", Number(e.target.value))} /></Field>
            <Field label="Current Value (KES)"><input type="number" min={0} className={inp} value={form.currentValue ?? ""} onChange={(e) => update("currentValue", Number(e.target.value))} /></Field>
          </div>
        </Card>

        <Card noPad>
          <CardHeader title="Notes" />
          <div className="p-5"><textarea rows={3} className={inp + " resize-none"} value={form.description ?? ""} onChange={(e) => update("description", e.target.value)} /></div>
        </Card>

        {save.error && <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{save.error}</p>}

        <div className="flex justify-end gap-3 pb-6">
          <Btn variant="secondary" type="button" onClick={() => navigate(`/assets/${id}`)}>Cancel</Btn>
          <Btn variant="primary" type="submit" loading={save.loading} loadingText="Saving…">Save Changes</Btn>
        </div>
      </form>
    </div>
  );
}
